import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { queryDocuments } from '../services/firebaseService';
import BonusItemsPopup from '../components/BonusItemsPopup';
import { confirm } from '../components/ui/confirm';
import { Headset, User, Tag, X, Pencil } from 'lucide-react';
import './CartPage.css';

const CartPage = () => {
  const navigate = useNavigate();
  const { 
    items, 
    appliedCoupon, 
    removeFromCart, 
    updateQuantity, 
    applyCoupon, 
    removeCoupon,
    clearCart,
    getCartTotal,
    getPartialPaymentTotal,
    getCODAmount,
    getDiscountAmount,
    getFinalTotal
  } = useCart();

  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(true);
  
  // Accordion state
  const [isShippingExpanded, setIsShippingExpanded] = useState(true);
  const [isDiscountExpanded, setIsDiscountExpanded] = useState(true);

  // Free items that are automatically added
  const freeItems = [
    {
      cartId: 'free-bag-bonus',
      id: 'free-bag',
      name: 'Premium Laptop Bag (Bonus)',
      imageUrls: ['/images/bonus/bag.jpg'],
      price: 0,
      quantity: 1,
      isFreeBonus: true
    },
    {
      cartId: 'free-antivirus-bonus',
      id: 'free-antivirus',
      name: 'Quick Heal Antivirus (Bonus)',
      imageUrls: ['/images/bonus/quickheal.webp'],
      price: 0,
      quantity: 1,
      isFreeBonus: true
    }
  ];

  // Fetch coupons from Firestore
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        setIsLoadingCoupons(true);
        const coupons = await queryDocuments('coupons', [
          { field: 'status', operator: '==', value: 'ACTIVE' }
        ]);
        
        const transformedCoupons = coupons.map(coupon => ({
          code: coupon.code,
          type: coupon.discountType === 'PERCENTAGE' ? 'percentage' : 'fixed',
          value: coupon.discountValue,
          minAmount: coupon.minOrderValue || 0,
          maxAmount: coupon.maxDiscountAmount || 0,
          description: coupon.description || `${coupon.title} - ${coupon.discountValue}${coupon.discountType === 'PERCENTAGE' ? '%' : '₹'} off`,
          usageLimit: coupon.usageLimit || 0,
          usedCount: coupon.usedCount || 0,
          firstTimeUserOnly: coupon.firstTimeUserOnly || false,
          expiryDate: coupon.expiryDate || 0
        }));
        
        setAvailableCoupons(transformedCoupons);
      } catch (error) {
        console.error('Error fetching coupons:', error);
        setAvailableCoupons([]);
      } finally {
        setIsLoadingCoupons(false);
      }
    };

    fetchCoupons();
  }, []);

  const formatPrice = (price) => {
    let numPrice = price;
    if (typeof price === 'string') {
      numPrice = parseFloat(price.replace(/[₹$,\s]/g, ''));
    }
    if (!numPrice || isNaN(numPrice) || numPrice <= 0) {
      return '₹0.00';
    }
    return `₹${Number(numPrice).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getItemPrice = (item) => {
    let price = item.price;
    if (item.selectedRAM) price += item.selectedRAM.priceIncrease;
    if (item.selectedStorage) price += item.selectedStorage.priceIncrease;
    return price;
  };

  const getItemTotal = (item) => {
    return getItemPrice(item) * item.quantity;
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError('');

    try {
      const coupon = availableCoupons.find(c => c.code.toLowerCase() === couponCode.toLowerCase());
      
      if (!coupon) {
        setCouponError('Invalid coupon code');
      } else if (coupon.expiryDate && coupon.expiryDate > 0 && Date.now() > coupon.expiryDate) {
        setCouponError('This coupon has expired');
      } else if (coupon.usageLimit > 0 && coupon.usedCount >= coupon.usageLimit) {
        setCouponError('This coupon has reached its usage limit');
      } else if (getCartTotal() < coupon.minAmount) {
        setCouponError(`Minimum order amount $${coupon.minAmount.toLocaleString()} required`);
      } else {
        applyCoupon(coupon);
        setCouponCode('');
        setCouponError('');
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      setCouponError('Failed to apply coupon. Please try again.');
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const handleClearAll = async () => {
    try {
      const ok = await confirm('Are you sure you want to clear all items from your cart?');
      if (ok) {
        clearCart();
        // Also remove coupon if applied
        if (appliedCoupon) {
          removeCoupon();
        }
      }
    } catch (e) {
      console.error('Confirm dialog failed:', e);
    }
  };

  const allItems = [...items, ...freeItems];
  
  if (items.length === 0) {
    return (
      <div className="cart-page empty">
        <div className="empty-cart-container">
          <div className="empty-content">
            <h1>Your Cart is Empty</h1>
            <p>Discover premium laptops and accessories.</p>
            <button 
              onClick={() => navigate('/')} 
              className="explore-button desktop-only"
            >
              <span>Continue Shopping</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-container">
        {/* Breadcrumb & Title */}
        <div className="cart-header">
          <div className="breadcrumb">
            <span onClick={() => navigate('/')} className="breadcrumb-link">Home</span>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-current">Cart</span>
          </div>
          <h1 className="cart-title">Shopping Cart</h1>
        </div>

        <div className="cart-content-wrapper">
          {/* Left Column: Cart Items */}
          <div className="cart-left-column">
            
            <div className="cart-table-header desktop-only">
              <div className="col-item">Item</div>
              <div className="col-price">Price</div>
              <div className="col-qty">Qty</div>
              <div className="col-subtotal">Subtotal</div>
              <div className="col-actions"></div>
            </div>

            <div className="cart-items-list">
              {allItems.map((item, index) => (
                <div key={item.cartId || index} className="cart-item-row">
                  <div className="col-item">
                    <div className="item-image-wrapper">
                      <img
                        src={item.imageUrls?.[0] || '/images/trending-image.jpeg'}
                        alt={item.name}
                        onClick={() => !item.isFreeBonus && navigate(`/product/${item.id}`)}
                        onError={(e) => { e.target.onerror = null; e.target.src = '/images/trending-image.jpeg'; }}
                        style={{ cursor: item.isFreeBonus ? 'default' : 'pointer' }}
                      />
                    </div>
                    <div className="item-info">
                      <div 
                        className="item-desc-container"
                        onClick={() => !item.isFreeBonus && navigate(`/product/${item.id}`)}
                        style={{ cursor: item.isFreeBonus ? 'default' : 'pointer' }}
                      >
                        <p className="item-desc" style={{ margin: 0, fontWeight: 600, color: '#1A1A1A' }}>
                          {item.name} {item.baseSpecs?.processor && `— ${item.baseSpecs.processor}`}
                        </p>
                        {item.isFreeBonus && (
                          <p style={{ fontSize: '12px', color: '#0063B8', margin: '4px 0 0 0', fontWeight: 600 }}>
                            Included Free Bonus
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-price mobile-row">
                    <span className="mobile-label">Price:</span>
                    <span className="price-value">{formatPrice(getItemPrice(item))}</span>
                  </div>
                  
                  <div className="col-qty mobile-row">
                    <span className="mobile-label">Qty:</span>
                    {item.isFreeBonus ? (
                      <div className="qty-control" style={{ background: 'transparent' }}>
                        <span className="qty-num">{item.quantity}</span>
                      </div>
                    ) : (
                      <div className="qty-control">
                        <button 
                          onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          className="qty-btn"
                        >
                          -
                        </button>
                        <span className="qty-num">{item.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                          className="qty-btn"
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="col-subtotal mobile-row">
                    <span className="mobile-label">Subtotal:</span>
                    <span className="subtotal-value">{item.isFreeBonus ? 'FREE' : formatPrice(getItemTotal(item))}</span>
                  </div>

                  <div className="col-actions">
                    {!item.isFreeBonus && (
                      <>
                        <button 
                          className="action-btn remove-btn" 
                          onClick={() => removeFromCart(item.cartId)}
                          title="Remove"
                        >
                          <X size={20} color="#A2A6B0" strokeWidth={2} />
                        </button>
                        <button 
                          className="action-btn"
                          onClick={() => navigate(`/product/${item.id}`)}
                          title="Edit"
                        >
                          <Pencil size={18} color="#A2A6B0" strokeWidth={2} />
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Upgrades Sub-Row */}
                  {!item.isFreeBonus && (item.selectedRAM || item.selectedStorage) && (
                    <div className="cart-item-upgrades-row">
                      {item.selectedRAM && (
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          <span style={{ fontWeight: 600, color: '#1A1A1A' }}>RAM Upgrade: </span>
                          {item.selectedRAM.name} (+{formatPrice(item.selectedRAM.priceIncrease)})
                        </div>
                      )}
                      {item.selectedStorage && (
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          <span style={{ fontWeight: 600, color: '#1A1A1A' }}>Storage Upgrade: </span>
                          {item.selectedStorage.name} (+{formatPrice(item.selectedStorage.priceIncrease)})
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Bottom Actions */}
            <div className="cart-bottom-actions">
              <div className="actions-left">
                <button className="btn-outline white-bg desktop-only" onClick={() => navigate('/')}>Continue Shopping</button>
                <button className="btn-solid desktop-only" style={{ backgroundColor: '#0156FF', color: 'white', borderColor: '#0156FF' }} onClick={handleClearAll}>Clear Shopping Cart</button>
              </div>
            </div>

          </div>

          {/* Right Column: Order Summary */}
          <div className="cart-right-column">
            <div className="summary-box">
              <h2 className="summary-title">Summary</h2>
              
              <div className="summary-accordion">
                {/* Shipping Accordion */}
                <div className="accordion-item">
                  <div className="accordion-header" onClick={() => setIsShippingExpanded(!isShippingExpanded)}>
                    <h3>Estimate Shipping and Tax</h3>
                    <span className={`accordion-icon ${isShippingExpanded ? 'expanded' : ''}`}>^</span>
                  </div>
                  {isShippingExpanded && (
                    <div className="accordion-content">
                      <p className="shipping-note">Enter your destination to get a shipping estimate.</p>
                      {/* You can add state/zip inputs here if needed in future */}
                    </div>
                  )}
                </div>

                <div className="accordion-divider"></div>

                {/* Discount Code Accordion */}
                <div className="accordion-item">
                  <div className="accordion-header" onClick={() => setIsDiscountExpanded(!isDiscountExpanded)}>
                    <h3>Apply Discount Code</h3>
                    <span className={`accordion-icon ${isDiscountExpanded ? 'expanded' : ''}`}>^</span>
                  </div>
                  {isDiscountExpanded && (
                    <div className="accordion-content">
                      {!appliedCoupon ? (
                        <>
                          <div className="discount-input-group">
                            <input
                              type="text"
                              placeholder="Enter discount code"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                              className={`discount-input ${couponError ? 'error' : ''}`}
                            />
                            <button 
                              onClick={handleApplyCoupon}
                              disabled={isApplyingCoupon}
                              className="btn-apply"
                            >
                              {isApplyingCoupon ? '...' : 'Apply'}
                            </button>
                            {couponError && <span className="discount-error">{couponError}</span>}
                          </div>
                          <p style={{ fontSize: '12px', color: '#666', marginTop: '12px', marginBottom: 0 }}>
                            Got a code? Try <strong style={{color: '#0063B8', fontWeight: 600}}>NEXA5</strong> for 5% off your entire order!
                          </p>
                        </>
                      ) : (
                        <div className="applied-discount">
                          <span className="code">{appliedCoupon.code}</span>
                          <button onClick={removeCoupon} className="remove-code">Remove</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Totals Breakdown */}
              <div className="summary-totals">
                <div className="total-row">
                  <span>Subtotal</span>
                  <span>{formatPrice(getCartTotal())}</span>
                </div>

                <div className="total-row">
                  <span>Shipping</span>
                  <span style={{ color: '#22c55e' }}>FREE</span>
                </div>

                <div className="total-row">
                  <span>Tax (GST)</span>
                  <span style={{ color: '#64748b' }}>Included</span>
                </div>

                {appliedCoupon && getDiscountAmount() > 0 && (
                  <div className="total-row discount">
                    <span>Discount ({appliedCoupon.code})</span>
                    <span style={{ color: '#dc2626' }}>-{formatPrice(getDiscountAmount())}</span>
                  </div>
                )}

                <div className="total-row order-total">
                  <span>Order Total</span>
                  <span className="total-amount">{formatPrice(getFinalTotal())}</span>
                </div>
              </div>

              {/* Checkout Buttons */}
              <div className="summary-actions">
                <button className="btn-proceed" onClick={handleCheckout}>
                  Proceed to Checkout
                </button>
              </div>

            </div>
          </div>
        </div>

      </div>
      
      {/* Features Section */}
      <section className="cart-features-section">
        <div className="cart-feature-item">
          <div className="feature-icon">
            <Headset size={32} color="#fff" />
          </div>
          <h3>Dedicated Support</h3>
          <p>Access our expert tech team for any setup or troubleshooting help, ensuring your device runs perfectly from day one.</p>
        </div>
        <div className="cart-feature-item">
          <div className="feature-icon">
            <User size={32} color="#fff" />
          </div>
          <h3>Sustainable Tech Choice</h3>
          <p>By choosing certified refurbished, you're helping reduce e-waste and supporting a greener future without sacrificing power.</p>
        </div>
        <div className="cart-feature-item">
          <div className="feature-icon">
            <Tag size={32} color="#fff" />
          </div>
          <h3>Unbeatable Value</h3>
          <p>Get top-tier professional laptops and desktops at a fraction of the retail cost, directly from the source.</p>
        </div>
      </section>
      
    </div>
  );
};


export default CartPage;
